<script setup>
import { computed } from "vue";
import useActionButtonsCommon from "@/composables/ActionButtonsCommon";

const { store, loading, _error, selectedDevices, selectedDevicesGrouped } =
  useActionButtonsCommon();

const formattedError = computed(() =>
  _error.value ? _error.value.replace(/\n/g, "<br />") : ""
);

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(["update:modelValue"]);

const closeModal = () => {
  _error.value = null;
  loading.value = false;
  emit("update:modelValue", false);
};

const unassignDevices = async () => {
  const payload = {
    macs: selectedDevices.value.map((s) => s.mac),
  };

  try {
    loading.value = true;

    await store.dispatch("windtagDevices/unassignWindtagDevices", payload);
    closeModal();
  } catch (error) {
    loading.value = false;

    const failedMacs = error?.failed_macs ?? [];
    const fourPointZeroErrors = error?.["4.0 Errors"] ?? [];
    const fourPointZeroMessage = error?.["4.0 Message"] ?? null;

    const messages = [];

    if (error?.message) messages.push(error.message);
    if (fourPointZeroMessage) messages.push(fourPointZeroMessage);
    if (failedMacs.length)
      messages.push(`Failed MACs: ${failedMacs.join(", ")}`);
    if (fourPointZeroErrors.length) {
      messages.push(
        ...fourPointZeroErrors.map((e) => e.message || JSON.stringify(e))
      );
    }

    _error.value =
      messages.join("\n\n") ||
      `Failed to unassign device${selectedDevices.value.length > 1 ? "s" : ""}`;
  }
};
</script>

<template>
  <div v-if="modelValue">
    <div class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-backdrop fade show" @click="closeModal"></div>
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h4 class="modal-title">
              Unassign Device{{ selectedDevices.length > 1 ? "s" : "" }}
            </h4>
            <button type="button" class="close" @click="closeModal">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body">
            <div
              v-for="[key, course] in Object.entries(selectedDevicesGrouped)"
              :key="key"
            >
              <p class="font-weight-bold">
                {{ key === "null" ? "Unassigned" : key }}
              </p>

              <ul class="list-unstyled mb-0 pl-3">
                <li v-for="device in course" :key="device.id">
                  Tag: {{ device.tag }} <br />
                  MAC: {{ device.mac }}
                </li>
              </ul>
            </div>

            <div
              v-if="_error"
              class="text-danger"
              style="max-height: 150px; overflow-y: auto; white-space: pre-wrap"
              v-html="formattedError"
            ></div>
          </div>

          <div class="modal-footer justify-content-end">
            <button type="button" class="btn btn-danger" @click="closeModal">
              CLOSE
            </button>
            <button
              v-if="!loading"
              type="button"
              class="btn btn-success"
              @click="unassignDevices"
              :disabled="!selectedDevices.length"
            >
              UNASSIGN DEVICE{{ selectedDevices.length > 1 ? "S" : "" }}
            </button>
            <div
              class="justify-content-center align-items-center"
              style="text-align: center"
            >
              <font-awesome-icon
                v-if="loading"
                :icon="['fas', 'spinner']"
                spin
                size="2x"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import "@/styles/modal.css";

.list-unstyled li {
  margin-bottom: 0.5rem;
}
</style>
